import {
  Close,
  CloseFullscreen,
  Minimize,
  OpenInFull,
} from '@mui/icons-material';
import {
  Card,
  CardContent,
  CardHeader,
  Grow,
  IconButton,
  Modal,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import React, { createContext, useContext, useMemo, useState } from 'react';

type UnderlyingType = {
  id: string;
  title: string;
  titleComponent?: React.ReactNode;
  subtitle: string;
  content: React.ReactNode;
  state: 'open' | 'minimized' | 'maximized';
  cardProps?: React.ComponentProps<typeof Card>;
  onClosed?: () => void;
};

type ModalSubContext = {
  closeModal: () => void;
};

type ModalContextType = {
  current: UnderlyingType[];
  onChange: React.Dispatch<React.SetStateAction<UnderlyingType[]>>;
};

const ModalContext = createContext({} as ModalContextType);

const CurrentModalContext = createContext({} as ModalSubContext);

export default function ModalProvider(props: React.PropsWithChildren) {
  const { children } = props;
  const [content, setContent] = useState<UnderlyingType[]>([]);

  const value = useMemo(
    () => ({
      current: content,
      onChange: setContent,
    }),
    [content],
  );

  return (
    <ModalContext.Provider value={value}>
      {children}
      {content.map(
        (item, index) =>
          item.state !== 'minimized' && (
            <Modal
              key={index}
              open={true}
              sx={{
                width: '100vw',
                height: '100vh',
                display: 'flex',
                justifyContent:
                  item.state === 'maximized' ? 'stretch' : 'center',
                alignItems: item.state === 'maximized' ? 'stretch' : 'center',
                padding: 2,
                '.MuiPaper-root': {
                  display: 'flex',
                  flexDirection: 'column',
                },
              }}
              onClose={() => {
                item.onClosed?.();
                setContent((prev) => prev.filter((i) => i.id !== item.id));
              }}
            >
              <Grow in={true}>
                <Card
                  {...item.cardProps}
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    flexGrow: item.state === 'maximized' ? '1 !important' : 0,
                    flexShrink: 1,
                    minWidth: 400,
                    ...item.cardProps?.sx,
                  }}
                >
                  {item.title && (
                    <CardHeader
                      sx={{
                        flexShrink: 0,
                        '& > *': {
                          flexShrink: 0,
                        },
                      }}
                      title={item.title}
                      subheader={item.subtitle}
                      action={
                        <Stack direction="row" gap={1}>
                          <IconButton
                            onClick={() => {
                              setContent((prev) =>
                                prev.map((i) =>
                                  i.id === item.id
                                    ? {
                                        ...i,
                                        state: 'minimized',
                                      }
                                    : i,
                                ),
                              );
                            }}
                          >
                            <Minimize />
                          </IconButton>
                          <IconButton
                            onClick={() => {
                              setContent((prev) =>
                                prev.map((i) =>
                                  i.id === item.id
                                    ? {
                                        ...i,
                                        state:
                                          i.state === 'maximized'
                                            ? 'open'
                                            : 'maximized',
                                      }
                                    : i,
                                ),
                              );
                            }}
                          >
                            {item.state === 'maximized' ? (
                              <CloseFullscreen />
                            ) : (
                              <OpenInFull />
                            )}
                          </IconButton>
                          <IconButton
                            onClick={() => {
                              item.onClosed?.();
                              setContent((prev) =>
                                prev.filter((i) => i.id !== item.id),
                              );
                            }}
                          >
                            <Close />
                          </IconButton>
                        </Stack>
                      }
                    />
                  )}
                  <CardContent
                    sx={{
                      pt: 0,
                      minHeight: 0,
                      display: 'flex',
                      flexGrow: item.state === 'maximized' ? 1 : 0,
                      flexDirection: 'column',
                      '& > *': {
                        overflowY: 'auto',
                      },
                    }}
                  >
                    <CurrentModalContext.Provider
                      value={{
                        closeModal: () => {
                          item.onClosed?.();
                          setContent((prev) =>
                            prev.filter((i) => i.id !== item.id),
                          );
                        },
                      }}
                    >
                      {item.content}
                    </CurrentModalContext.Provider>
                  </CardContent>
                </Card>
              </Grow>
            </Modal>
          ),
      )}
      <Stack
        sx={{
          position: 'fixed',
          display: 'flex',
          bottom: 40,
          right: 0,
        }}
      >
        {content.map(
          (item, index) =>
            item.state === 'minimized' && (
              <Paper
                elevation={4}
                key={index}
                sx={{
                  px: 2,
                  py: 1,
                  borderTopLeftRadius: 8,
                  borderTopRightRadius: 8,
                }}
              >
                <Stack
                  direction="row"
                  minWidth={150}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack>
                    <Typography>WINDOW</Typography>
                  </Stack>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setContent((prev) =>
                        prev.map((i) =>
                          i.id === item.id
                            ? {
                                ...i,
                                state: 'open',
                              }
                            : i,
                        ),
                      );
                    }}
                  >
                    <OpenInFull />
                  </IconButton>
                </Stack>
              </Paper>
            ),
        )}
      </Stack>
    </ModalContext.Provider>
  );
}

export function useModal() {
  const context = useContext(ModalContext);

  return {
    showModal: (
      title: UnderlyingType['title'],
      content: UnderlyingType['content'],
      subtitle: UnderlyingType['subtitle'] = '',
      cardProps: UnderlyingType['cardProps'] = {},
      onClosed: UnderlyingType['onClosed'] = () => {},
      titleComponent: UnderlyingType['titleComponent'] = undefined,
    ) => {
      const id = window.crypto.randomUUID();
      context.onChange((prev) => [
        ...prev,
        {
          id,
          title,
          subtitle,
          content,
          cardProps,
          onClosed,
          state: 'open',
          titleComponent,
        },
      ]);
      return id;
    },
  };
}

export function useCurrentModal() {
  const context = useContext(CurrentModalContext);

  if (!context) {
    return null;
  }

  return context;
}
